import _ from "lodash";
import { toast } from "react-toastify";
import { getSubdomain } from "../config/StylesheetInjector";
import {
  APPSTORE,
  APP_LINK,
  LAST,
  PLAYSTORE,
  START,
} from "../constants/common";
import { store } from "../redux/Store";

/**
 * Opens a given URL in a new browser tab.
 *
 * @param {string} url - The URL to be opened in the new tab.
 */
export const openInNewTab = (url, target) => {
  if (!url) {
    return;
  }

  const newWindow = window.open(url, target || "_blank", "noopener,noreferrer");
  if (newWindow) {
    newWindow.opener = null;
  }
};

/**
 * Gets the iOS app link based on the current subdomain.
 *
 * @returns {string} The URL to the iOS app.
 */
export const getIOSAppLink = () => {
  return APP_LINK[`${getSubdomain()}_${APPSTORE}`];
};

/**
 * Gets the Android app link based on the current subdomain.
 *
 * @returns {string} The URL to the Android app.
 */
export const getAndroidAppLink = () => {
  return APP_LINK[`${getSubdomain()}_${PLAYSTORE}`];
};

/**
 * Scrolls the window to the top of the page with smooth behavior.
 *
 * This function scrolls the window to the top of the page.
 * The scroll behavior can be customized by changing the 'behavior' property to 'auto' or 'smooth'.
 */
export const scrollToTop = () => {
  window.scrollTo({
    top: 0,
    behavior: "smooth", // Optional: 'auto' or 'smooth'
  });
};

/**
 * Display a toast notification.
 *
 * @param {string} message - The message to display in the toast.
 * @param {Object} [options] - Optional settings for the toast.
 * @param {'info' | 'success' | 'warning' | 'error'} [options.type='info'] - The type of toast to display.
 * @param {'top-right' | 'top-center' | 'top-left' | 'bottom-right' | 'bottom-center' | 'bottom-left'} [options.position='top-right'] - The position of the toast on the screen.
 * @param {number} [options.autoClose=5000] - The delay in milliseconds to close the toast automatically.
 * @param {boolean} [options.hideProgressBar=false] - Hide the progress bar.
 * @param {boolean} [options.closeOnClick=true] - Close the toast when it's clicked.
 * @param {boolean} [options.pauseOnHover=true] - Pause the toast when it's hovered over.
 * @param {boolean} [options.draggable=true] - Make the toast draggable.
 * @param {boolean} [options.rtl=false] - Enable right-to-left text.
 */
export const notifyToaster = (message, options = {}) => {
  const {
    type = "info",
    position = "top-right",
    autoClose = 5000,
    hideProgressBar = false,
    closeOnClick = true,
    pauseOnHover = true,
    draggable = true,
    rtl = false,
  } = options;

  toast(message, {
    type,
    position,
    autoClose,
    hideProgressBar,
    closeOnClick,
    pauseOnHover,
    draggable,
    rtl,
  });
};

export const handleSaveAndExit = () => {
  const { authentication_token } = store.getState()?.personalInfo?.personalInfo;
  window.open(
    `${process.env.REACT_APP_FUELMAN_DOMAIN}?key=${authentication_token}`,
      "_self"
  );
};

/**
 * Removes key-value pairs with null values from an object.
 *
 * This function takes an object and removes any key-value pairs where the value is null.
 * It uses Lodash's `omitBy` function to achieve this.
 *
 * @param {Object} obj - The object from which to remove null key-value pairs.
 * @returns {Object} - A new object with null key-value pairs removed.
 *
 * @example
 * const obj = { a: 1, b: null, c: 3 };
 * const result = removeNullValues(obj);
 * console.log(result); // { a: 1, c: 3 }
 */
export const removeNullValues = (obj) => {
  return _.omitBy(obj, _.isNull);
};

/**
 * Extracts a substring from the given string based on the provided number of characters and direction.
 *
 * @param {string} str - The input string.
 * @param {number} num - The number of characters to extract.
 * @param {string} direction - The direction to extract from, either 'start' or 'last'.
 * @returns {string} - The extracted substring.
 *
 * @example
 * // Extracts the first 4 characters from the string 'vehicle'
 * extractSubstring('vehicle', 4, 'start'); // Returns 'vehi'
 *
 * @example
 * // Extracts the last 4 characters from the string 'vehicle'
 * extractSubstring('vehicle', 4, 'last'); // Returns 'hicle'
 */
export const extractSubstring = (str, num, direction = LAST) => {
  if (direction === START) {
    return str.slice(0, num);
  } else if (direction === LAST) {
    return str.slice(-num);
  }
};
